/**
 * useMentionSearch
 * Detects `@query` patterns in a textarea value at the current cursor position
 * and returns product suggestions from the catalog.
 *
 * While typing: inserts a friendly `@Product Name` token.
 * On submit: call serializeForSubmit() to convert to `@[Name](id)` for storage.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import api from "../utils/api/axiosInstance";
import useDebounce from "./useDebounce";
import {
  buildMentionDisplayToken,
  serializeMentionsForSubmit,
  storedMentionsToDisplayText,
} from "../utils/renderMentionText";

// Match @mention at end of text before cursor (product names may include digits, hyphens, etc.)
const MENTION_REGEX = /@([^\s@]*)$/;

export function useMentionSearch(value, setValue, inputRef) {
  const [mentionQuery, setMentionQuery] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [cursorPos, setCursorPos] = useState(null);
  const debouncedQuery = useDebounce(mentionQuery, 280);
  const fetchController = useRef(null);
  const mentionRegistryRef = useRef(new Map());

  const handleInput = useCallback(
    (e) => {
      const textarea = e?.target || inputRef?.current;
      if (!textarea) return;
      const pos = textarea.selectionStart;
      const textBefore = (textarea.value || value || "").slice(0, pos);
      const match = textBefore.match(MENTION_REGEX);
      if (match) {
        setMentionQuery(match[1]);
        setCursorPos(pos);
      } else {
        setMentionQuery(null);
        setSuggestions([]);
      }
    },
    [value, inputRef],
  );

  useEffect(() => {
    if (debouncedQuery === null) {
      setSuggestions([]);
      return;
    }

    if (fetchController.current) fetchController.current.abort();
    const controller = new AbortController();
    fetchController.current = controller;

    if (debouncedQuery.trim().length === 0) {
      setSuggestions([]);
      return;
    }

    api
      .get("/products/search", {
        params: { query: debouncedQuery.trim() },
        signal: controller.signal,
      })
      .then((res) => {
        const products = Array.isArray(res.data)
          ? res.data
          : res.data?.products || res.data?.results || [];
        setSuggestions(products.slice(0, 6));
      })
      .catch((err) => {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          setSuggestions([]);
        }
      });

    return () => controller.abort();
  }, [debouncedQuery]);

  const insertMention = useCallback(
    (product) => {
      if (!inputRef?.current || cursorPos === null || !product?._id) return;
      const textarea = inputRef.current;
      const currentValue = textarea.value;
      const textBefore = currentValue.slice(0, cursorPos);
      const textAfter = currentValue.slice(cursorPos);
      const withoutQuery = textBefore.replace(MENTION_REGEX, "");

      const displayToken = buildMentionDisplayToken(product.name);
      mentionRegistryRef.current.set(displayToken, {
        id: product._id,
        name: product.name,
      });

      const mention = `${displayToken} `;
      const newValue = withoutQuery + mention + textAfter;
      setValue(newValue);
      setMentionQuery(null);
      setSuggestions([]);

      requestAnimationFrame(() => {
        const newPos = withoutQuery.length + mention.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      });
    },
    [cursorPos, inputRef, setValue],
  );

  const serializeForSubmit = useCallback((text) => {
    return serializeMentionsForSubmit(text, mentionRegistryRef.current);
  }, []);

  const clearMentionRegistry = useCallback(() => {
    mentionRegistryRef.current.clear();
  }, []);

  /** Load stored `@[Name](id)` caption/comment text into display tokens + registry. */
  const seedRegistryFromStored = useCallback((storedText, nameById = {}) => {
    mentionRegistryRef.current.clear();
    const { displayText, registry } = storedMentionsToDisplayText(
      storedText,
      nameById,
    );
    registry.forEach((value, key) => {
      mentionRegistryRef.current.set(key, value);
    });
    return displayText;
  }, []);

  const clearMention = useCallback(() => {
    setMentionQuery(null);
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    mentionQuery,
    insertMention,
    handleInput,
    clearMention,
    serializeForSubmit,
    clearMentionRegistry,
    seedRegistryFromStored,
  };
}
